/** Building blocks of the practice player (compact dock + expanded sheet + audio-only panel). */
import { LoaderCircle, Minus, Pause, Play, Plus, RefreshCw, Repeat, RotateCcw, RotateCw, X } from 'lucide-react';
import { useRef, useState, type KeyboardEvent } from 'react';
import { audienceSpecificity, groupsFromMask, type Section } from '../../shared/audience';
import type { SongFileDTO } from '../../shared/dto/library';
import { useMe } from '../auth/AuthProvider';
import { SECTION_COLOR } from '../components/people';
import { Button, cx, IconButton } from '../components/ui';
import { useI18n, useT } from '../i18n';
import { useAudio, useAudioTime } from './AudioProvider';
import { formatTime, isLoopActive, RATE_MAX, RATE_MIN, RATE_STEP, SKIP_SECONDS } from './logic';

/** First section the track is aimed at (for its colour dot), or null for whole-choir tracks. */
export function trackSection(mask: number): Section | null {
  const g = groupsFromMask(mask).find((x) => x !== 'all');
  return (g as Section | undefined) ?? null;
}

export function useRateLabel(): (rate: number) => string {
  const { loc } = useI18n();
  const t = useT();
  return (rate) => t('viewer.audio_speed_value', { rate: new Intl.NumberFormat(loc, { maximumFractionDigits: 2 }).format(rate) });
}

export function TrackDot({ track }: { track: SongFileDTO }) {
  const s = trackSection(track.audienceMask);
  return (
    <span
      aria-hidden
      className="inline-block size-3 shrink-0 rounded-full"
      style={{ background: s ? SECTION_COLOR[s] : 'var(--accent)' }}
    />
  );
}

export function PlayButton({ size = 'md' }: { size?: 'md' | 'lg' }) {
  const t = useT();
  const { playing, buffering, toggle, current } = useAudio();
  const Icon = buffering && playing ? LoaderCircle : playing ? Pause : Play;
  return (
    <button
      type="button"
      onClick={toggle}
      disabled={!current}
      aria-label={playing ? t('viewer.audio_pause') : t('viewer.audio_play')}
      title={playing ? t('viewer.audio_pause') : t('viewer.audio_play')}
      className={cx(
        'inline-flex shrink-0 items-center justify-center rounded-full bg-[var(--accent)] text-[var(--accent-ink)] shadow transition active:scale-95 disabled:opacity-40',
        size === 'lg' ? 'size-20' : 'size-14',
      )}
    >
      <Icon
        className={cx(size === 'lg' ? 'size-10' : 'size-7', buffering && playing && 'animate-spin', !playing && 'translate-x-0.5')}
        fill={playing || buffering ? 'none' : 'currentColor'}
        aria-hidden
      />
    </button>
  );
}

export function SkipButton({ dir, size = 'md' }: { dir: 1 | -1; size?: 'md' | 'lg' }) {
  const t = useT();
  const { skip, current } = useAudio();
  const Icon = dir < 0 ? RotateCcw : RotateCw;
  const label = dir < 0 ? t('viewer.audio_back5') : t('viewer.audio_fwd5');
  return (
    <button
      type="button"
      onClick={() => skip(dir * SKIP_SECONDS)}
      disabled={!current}
      aria-label={label}
      title={label}
      className={cx(
        'relative inline-flex shrink-0 items-center justify-center rounded-full text-[var(--ink-2)] transition hover:bg-[var(--accent-soft)] active:bg-[var(--accent-soft)] disabled:opacity-40',
        size === 'lg' ? 'size-16' : 'size-12',
      )}
    >
      <Icon className={size === 'lg' ? 'size-9' : 'size-7'} aria-hidden />
      <span className="absolute text-[11px] font-bold" aria-hidden>
        {SKIP_SECONDS}
      </span>
    </button>
  );
}

/**
 * A slider released by finger or mouse gives its focus back, so page-turn keys and Bluetooth pedals
 * (ignored while an <input> has focus) turn pages again instead of moving the slider. Keyboard
 * users keep focus.
 */
function releaseFocus(e: { currentTarget: HTMLInputElement }) {
  e.currentTarget.blur();
}

/** Seek slider: local value while dragging, one seek on release (avoids a Range request per pixel). */
export function SeekBar({ showTimes = true, className }: { showTimes?: boolean; className?: string }) {
  const t = useT();
  const { seek, current } = useAudio();
  const { time, duration } = useAudioTime();
  const [drag, setDrag] = useState<number | null>(null);
  // Mirrors `drag` synchronously: pointerup + touchend + blur each commit, but only the first seeks.
  const dragRef = useRef<number | null>(null);
  const max = duration > 0 ? duration : 1;
  const value = Math.min(max, drag ?? time);
  const commit = () => {
    const v = dragRef.current;
    dragRef.current = null;
    if (v !== null) seek(v);
    setDrag(null);
  };
  const commitAndRelease = (e: { currentTarget: HTMLInputElement }) => {
    commit();
    releaseFocus(e);
  };
  return (
    <div className={cx('flex items-center gap-3', className)}>
      {showTimes ? <span className="w-14 shrink-0 text-right text-[15px] tabular-nums text-[var(--muted)]">{formatTime(value)}</span> : null}
      <input
        type="range"
        min={0}
        max={max}
        step={0.1}
        value={value}
        disabled={!current}
        aria-label={t('viewer.audio_seek')}
        aria-valuetext={t('viewer.audio_time', { time: formatTime(value), duration: formatTime(duration) })}
        onChange={(e) => {
          const v = Number(e.currentTarget.value);
          dragRef.current = v;
          setDrag(v);
        }}
        onPointerUp={commitAndRelease}
        onTouchEnd={commitAndRelease}
        onKeyUp={(e: KeyboardEvent<HTMLInputElement>) => {
          if (e.key.startsWith('Arrow') || e.key === 'Home' || e.key === 'End' || e.key.startsWith('Page')) commit();
        }}
        onBlur={commit}
        className="h-11 min-w-0 flex-1 cursor-pointer accent-[var(--accent)]"
      />
      {showTimes ? <span className="w-14 shrink-0 text-[15px] tabular-nums text-[var(--muted)]">{formatTime(duration)}</span> : null}
    </div>
  );
}

export function TrackList() {
  const t = useT();
  const me = useMe();
  const { tracks, current, select } = useAudio();
  if (tracks.length === 0) return <p className="text-[var(--muted)]">{t('viewer.audio_none')}</p>;
  return (
    <div className="flex flex-col gap-2">
      <span className="text-[15px] font-semibold text-[var(--ink-2)]">{t('viewer.audio_tracks')}</span>
      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={t('viewer.audio_tracks')}>
        {tracks.map((tr) => {
          const on = tr.id === current?.id;
          const own = me?.section ? audienceSpecificity(tr.audienceMask, me.section) === 2 : false;
          return (
            <button
              key={tr.id}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => select(tr.id)}
              className={cx(
                'inline-flex min-h-12 items-center gap-2 rounded-full border px-4 text-[17px] font-medium transition',
                on ? 'border-transparent bg-[var(--accent)] text-[var(--accent-ink)]' : 'border-[var(--line)] bg-[var(--surface)] text-[var(--ink)]',
              )}
            >
              <TrackDot track={tr} />
              <span>{tr.title}</span>
              {own ? (
                <span className={cx('text-[13px] font-semibold', on ? 'opacity-80' : 'text-[var(--muted)]')}>· {t('viewer.audio_own_section')}</span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function SpeedControl() {
  const t = useT();
  const rateLabel = useRateLabel();
  const { rate, setRate } = useAudio();
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-3">
        <span className="text-[15px] font-semibold text-[var(--ink-2)]">
          {t('viewer.audio_speed')}: <span className="tabular-nums text-[var(--ink)]">{rateLabel(rate)}</span>
        </span>
        {rate !== 1 ? (
          <Button variant="ghost" onClick={() => setRate(1)}>
            {t('viewer.audio_speed_reset')}
          </Button>
        ) : null}
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setRate(rate - RATE_STEP)}
          disabled={rate <= RATE_MIN}
          aria-label={t('viewer.audio_slower')}
          title={t('viewer.audio_slower')}
          className="inline-flex size-12 shrink-0 items-center justify-center rounded-full border border-[var(--line)] bg-[var(--surface)] disabled:opacity-40"
        >
          <Minus className="size-6" aria-hidden />
        </button>
        <input
          type="range"
          min={RATE_MIN}
          max={RATE_MAX}
          step={RATE_STEP}
          value={rate}
          onChange={(e) => setRate(Number(e.currentTarget.value))}
          onPointerUp={releaseFocus}
          onTouchEnd={releaseFocus}
          aria-label={t('viewer.audio_speed')}
          aria-valuetext={rateLabel(rate)}
          className="h-11 min-w-0 flex-1 cursor-pointer accent-[var(--accent)]"
        />
        <button
          type="button"
          onClick={() => setRate(rate + RATE_STEP)}
          disabled={rate >= RATE_MAX}
          aria-label={t('viewer.audio_faster')}
          title={t('viewer.audio_faster')}
          className="inline-flex size-12 shrink-0 items-center justify-center rounded-full border border-[var(--line)] bg-[var(--surface)] disabled:opacity-40"
        >
          <Plus className="size-6" aria-hidden />
        </button>
      </div>
    </div>
  );
}

export function LoopControl() {
  const t = useT();
  const { loop, markA, markB, clearLoop, current } = useAudio();
  const active = isLoopActive(loop);
  return (
    <div className="flex flex-col gap-2">
      <span className="flex items-center gap-2 text-[15px] font-semibold text-[var(--ink-2)]">
        <Repeat className="size-5" aria-hidden />
        {active ? t('viewer.audio_loop_on', { a: formatTime(loop.a), b: formatTime(loop.b) }) : t('viewer.audio_loop')}
      </span>
      <div className="flex flex-wrap gap-2">
        <Button variant={loop.a !== null ? 'primary' : 'secondary'} onClick={markA} disabled={!current}>
          {t('viewer.audio_set_a')}
          {loop.a !== null ? <span className="tabular-nums opacity-80">{formatTime(loop.a)}</span> : null}
        </Button>
        <Button variant={loop.b !== null ? 'primary' : 'secondary'} onClick={markB} disabled={!current}>
          {t('viewer.audio_set_b')}
          {loop.b !== null ? <span className="tabular-nums opacity-80">{formatTime(loop.b)}</span> : null}
        </Button>
        {loop.a !== null || loop.b !== null ? (
          <Button variant="ghost" icon={X} onClick={clearLoop}>
            {t('viewer.audio_clear_loop')}
          </Button>
        ) : null}
      </div>
      {!active ? <p className="text-[15px] text-[var(--muted)]">{t('viewer.audio_loop_hint')}</p> : null}
    </div>
  );
}

export function AudioErrorLine({ compact }: { compact?: boolean }) {
  const t = useT();
  const { error, retry } = useAudio();
  if (!error) return null;
  return (
    <div role="alert" className={cx('flex items-center gap-2 text-[var(--danger-text)]', compact ? 'text-[15px]' : 'text-[16px]')}>
      <span className="min-w-0 flex-1 truncate">{error === 'offline' ? t('viewer.audio_offline') : t('viewer.audio_error')}</span>
      {compact ? (
        // The compact dock row is narrow on phones: icon-only, still a 44 pt target.
        <IconButton icon={RefreshCw} label={t('common.retry')} onClick={retry} className="ring-1 ring-[var(--line)]" />
      ) : (
        <Button variant="secondary" icon={RefreshCw} onClick={retry} className="shrink-0">
          {t('common.retry')}
        </Button>
      )}
    </div>
  );
}

/** Everything: tracks, seek, transport, speed, A–B loop (expanded sheet and audio-only songs). */
export function AudioFullControls() {
  return (
    <div className="flex flex-col gap-6 py-2">
      <TrackList />
      <div className="flex flex-col gap-3">
        <SeekBar />
        <div className="flex items-center justify-center gap-6">
          <SkipButton dir={-1} size="lg" />
          <PlayButton size="lg" />
          <SkipButton dir={1} size="lg" />
        </div>
        <AudioErrorLine />
      </div>
      <SpeedControl />
      <LoopControl />
    </div>
  );
}
